import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { safeFormatDate } from '../lib/dateTime';
import {
  CalendarDays,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Building2,
  MapPin,
  Info,
  AlertTriangle,
  Globe,
  Flag,
  Edit2,
  User,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

export default function HolidaysPage() {
  const { api, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedState, setSelectedState] = useState('');
  const [states, setStates] = useState([]);
  const [holidays, setHolidays] = useState({});
  const [sources, setSources] = useState({});
  const [customHolidays, setCustomHolidays] = useState([]);
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    date: '',
    name: '',
    scope: 'company',
    user_id: '',
    is_exclusion: false,
  });

  const isAdmin = user?.role === 'admin';
  const isLegacyYear = settings?.holiday_calculation_start_date && 
    `${year}-01-01` < settings.holiday_calculation_start_date;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedState) {
      fetchHolidays();
    }
  }, [year, selectedState]);

  const fetchInitialData = async () => {
    try {
      const [statesRes, settingsRes] = await Promise.all([
        api.get('/holidays/states'),
        isAdmin ? api.get('/holidays/settings').catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      
      setStates(statesRes.data);
      setSettings(settingsRes.data);
      
      // Set initial state from user or company default
      const userState = user?.state;
      const defaultState = settingsRes.data?.default_state || 'NI';
      setSelectedState(userState || defaultState);
      
      // Fetch users for admin
      if (isAdmin) {
        const usersRes = await api.get('/users');
        setUsers(usersRes.data.filter(u => u.is_active !== false));
      }
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
      toast.error('Fehler beim Laden der Daten');
    }
  };

  const fetchHolidays = async () => {
    setIsLoading(true);
    try {
      const [holidaysRes, customsRes] = await Promise.all([
        api.get(`/holidays/${year}?state=${selectedState}`),
        isAdmin ? api.get(`/holidays/custom?year=${year}`) : Promise.resolve({ data: [] }),
      ]);
      
      setHolidays(holidaysRes.data.holidays);
      setSources(holidaysRes.data.sources || {});
      setCustomHolidays(customsRes.data);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
      toast.error('Fehler beim Laden der Feiertage');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    
    if (!formData.date || !formData.name) {
      toast.error('Bitte Datum und Bezeichnung angeben');
      return;
    }

    try {
      await api.post('/holidays/custom', {
        date: formData.date,
        name: formData.name,
        state: selectedState,
        scope: formData.scope,
        user_id: formData.scope === 'user' ? formData.user_id : null,
        is_exclusion: formData.is_exclusion,
      });
      
      toast.success(formData.is_exclusion 
        ? 'Feiertagsausschluss erstellt' 
        : 'Feiertag erstellt'
      );
      setIsAddDialogOpen(false);
      setFormData({ date: '', name: '', scope: 'company', user_id: '', is_exclusion: false });
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const openDeleteDialog = (holiday) => {
    setHolidayToDelete(holiday);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteHoliday = async () => {
    if (!holidayToDelete) return;
    
    try {
      await api.delete(`/holidays/custom/${holidayToDelete.id}`);
      toast.success('Feiertag gelöscht');
      setIsDeleteDialogOpen(false);
      setHolidayToDelete(null);
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const getSourceBadge = (source) => {
    const styles = {
      federal: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Bundesweit', icon: Globe },
      state: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', label: 'Landesfeiertag', icon: Flag },
      special: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', label: 'Sonderfeiertag', icon: CalendarDays },
      custom: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500/30', label: 'Manuell ergänzt', icon: Edit2 },
      legacy: { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border/30', label: 'Alte Berechnung', icon: Info },
    };
    const s = styles[source] || styles.federal;
    const Icon = s.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${s.bg} ${s.text} ${s.border}`}>
        <Icon className="w-3 h-3" />
        {s.label}
      </span>
    );
  };

  const getWeekday = (dateStr) => {
    return safeFormatDate(dateStr, 'EEEE');
  };

  const sortedHolidays = Object.entries(holidays)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, name]) => ({ date, name, source: sources[date] || 'federal' }));

  const getStateName = (code) => {
    const state = states.find(s => s.code === code);
    return state?.name || code;
  };

  if (isLoading && !holidays) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="holidays-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Feiertage</h1>
        <p className="text-muted-foreground mt-1">
          Gesetzliche und benutzerdefinierte Feiertage für die Arbeitszeitberechnung
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Year Selector */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setYear(y => y - 1)}
              className="border-border text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xl font-mono font-bold text-foreground w-16 text-center">{year}</span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setYear(y => y + 1)}
              className="border-border text-muted-foreground hover:text-foreground"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* State Selector */}
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="w-[200px] bg-input border-border text-foreground">
                <SelectValue placeholder="Bundesland wählen" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border max-h-[300px]">
                {states.map((state) => (
                  <SelectItem key={state.code} value={state.code}>
                    {state.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Admin Actions */}
        {isAdmin && (
          <Button
            onClick={() => {
              setFormData({ 
                date: `${year}-01-01`, 
                name: '', 
                scope: 'company', 
                user_id: '', 
                is_exclusion: false 
              });
              setIsAddDialogOpen(true);
            }}
            className="bg-indigo-500 hover:bg-indigo-600 text-white"
            data-testid="add-holiday-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Feiertag ergänzen
          </Button>
        )}
      </div>

      {/* Legacy Warning */}
      {isLegacyYear && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-600 dark:text-amber-400 font-medium">Älterer Zeitraum</p>
              <p className="text-amber-600 dark:text-amber-400/80 text-sm mt-1">
                Für diesen Zeitraum gilt noch die frühere Berechnung ohne landesspezifische Feiertage.
                Ab {safeFormatDate(settings.holiday_calculation_start_date, 'd. MMMM yyyy')} werden 
                die neuen Regeln angewendet.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Info Card */}
      <div className="card-glass p-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-indigo-500/10">
            <CalendarDays className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <p className="text-foreground font-medium">
              {sortedHolidays.length} Feiertag{sortedHolidays.length !== 1 ? 'e' : ''} in {getStateName(selectedState)} ({year})
            </p>
            <p className="text-sm text-muted-foreground">
              {sortedHolidays.filter(h => h.source === 'federal').length} bundesweit, {' '}
              {sortedHolidays.filter(h => h.source === 'state').length} landesspezifisch
              {sortedHolidays.filter(h => h.source === 'custom').length > 0 && 
                `, ${sortedHolidays.filter(h => h.source === 'custom').length} manuell ergänzt`}
            </p>
          </div>
        </div>
      </div>

      {/* Holidays Table */}
      <div className="card-glass overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Datum</th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Wochentag</th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Bezeichnung</th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Herkunft</th>
              {isAdmin && (
                <th className="text-right p-4 text-xs font-medium text-muted-foreground uppercase">Aktionen</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {isLoading ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} className="p-8 text-center">
                  <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : sortedHolidays.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 5 : 4} className="p-8 text-center text-muted-foreground">
                  Keine Feiertage für dieses Jahr und Bundesland gefunden
                </td>
              </tr>
            ) : (
              sortedHolidays.map((holiday) => {
                const isCustom = holiday.source === 'custom';
                const customHoliday = customHolidays.find(c => c.date === holiday.date);
                
                return (
                  <motion.tr
                    key={holiday.date}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-accent/20"
                  >
                    <td className="p-4 text-foreground font-mono">
                      {safeFormatDate(holiday.date, 'd. MMM')}
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {getWeekday(holiday.date)}
                    </td>
                    <td className="p-4">
                      <span className={`text-foreground ${holiday.source === 'state' ? 'font-medium' : ''}`}>
                        {holiday.name}
                      </span>
                      {customHoliday?.scope === 'user' && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <User className="w-3 h-3 text-muted-foreground inline ml-2" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Nur für einen Mitarbeiter</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </td>
                    <td className="p-4">
                      {getSourceBadge(holiday.source)}
                    </td>
                    {isAdmin && (
                      <td className="p-4 text-right">
                        {isCustom && customHoliday && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openDeleteDialog(customHoliday)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-holiday-${holiday.date}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        {!isCustom && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setFormData({
                                      date: holiday.date,
                                      name: holiday.name,
                                      scope: 'company',
                                      user_id: '',
                                      is_exclusion: true,
                                    });
                                    setIsAddDialogOpen(true);
                                  }}
                                  className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                                  data-testid={`exclude-holiday-${holiday.date}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Feiertag ausschließen</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </td>
                    )}
                  </motion.tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Custom Holidays Section (Admin only) */}
      {isAdmin && customHolidays.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-foreground">Benutzerdefinierte Feiertage</h2>
          <div className="grid gap-3">
            {customHolidays.map((custom) => (
              <motion.div
                key={custom.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-glass p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${custom.is_exclusion ? 'bg-red-500/10' : 'bg-indigo-500/10'}`}>
                    {custom.is_exclusion ? (
                      <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                    ) : (
                      <CalendarDays className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-foreground font-medium">
                      {custom.name}
                      {custom.is_exclusion && (
                        <span className="text-red-600 dark:text-red-400 text-sm ml-2">(Ausschluss)</span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {safeFormatDate(custom.date, 'd. MMMM yyyy')}
                      {custom.state && ` · ${getStateName(custom.state)}`}
                      {custom.scope === 'user' && ' · Einzelner Mitarbeiter'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDeleteDialog(custom)}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Add Holiday Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {formData.is_exclusion ? 'Feiertag ausschließen' : 'Feiertag ergänzen'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {formData.is_exclusion 
                ? 'Entfernt einen berechneten Feiertag für regionale Sonderfälle.'
                : 'Fügt einen zusätzlichen Feiertag hinzu, der nicht automatisch berechnet wird.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddHoliday} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Datum</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="bg-input border-border text-foreground"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Bezeichnung</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Augsburger Friedensfest"
                required
                className="bg-input border-border text-foreground"
              />
            </div>

            {!formData.is_exclusion && (
              <>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Gilt für</Label>
                  <Select 
                    value={formData.scope} 
                    onValueChange={(v) => setFormData({ ...formData, scope: v, user_id: '' })}
                  >
                    <SelectTrigger className="bg-input border-border text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="company">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Gesamtes Unternehmen
                        </div>
                      </SelectItem>
                      <SelectItem value="user">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Einzelner Mitarbeiter
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.scope === 'user' && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Mitarbeiter</Label>
                    <Select 
                      value={formData.user_id} 
                      onValueChange={(v) => setFormData({ ...formData, user_id: v })}
                    >
                      <SelectTrigger className="bg-input border-border text-foreground">
                        <SelectValue placeholder="Mitarbeiter wählen" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border max-h-[200px]">
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.first_name} {u.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground">
                <Info className="w-4 h-4 inline mr-2" />
                {formData.is_exclusion 
                  ? `Der Feiertag "${formData.name}" wird für ${getStateName(selectedState)} entfernt.`
                  : `Der Feiertag gilt für ${getStateName(selectedState)}.`}
              </p>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsAddDialogOpen(false)} 
                className="border-border text-muted-foreground"
              >
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                className={formData.is_exclusion 
                  ? "bg-amber-500 hover:bg-amber-600 text-white"
                  : "bg-indigo-500 hover:bg-indigo-600 text-white"
                }
              >
                {formData.is_exclusion ? 'Ausschließen' : 'Hinzufügen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              Feiertag löschen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-muted-foreground">
              Möchten Sie den Feiertag <span className="font-medium text-foreground">&quot;{holidayToDelete?.name}&quot;</span> wirklich löschen?
            </p>
            
            {holidayToDelete?.is_exclusion && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Dies ist ein Ausschluss. Nach dem Löschen gilt der ursprüngliche Feiertag wieder.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              className="border-border text-muted-foreground"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleDeleteHoliday}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
